import React from 'react';
import { ActionText } from '@dsg-tech/homefield-react';
import {
  FulfillmentButtonState,
  QuickviewDetails,
  SameDaySkuAvailabilityDetails,
  SkuDetails,
  SkuStoreAvailability,
} from '../types';

export const getShippingMethodState = (
  sameDayDeliveryEnabled: boolean,
  sku: SkuDetails,
  quickviewDetails: QuickviewDetails,
  count: number,
  deliveryZip: string | null,
): FulfillmentButtonState => {
  if (!sku) {
    if (quickviewDetails && quickviewDetails.style && quickviewDetails.style.buyable === 0) {
      return {
        title: 'Ship',
        subText: 'In-store purchase only',
        disabled: true,
        valid: false,
        type: 'ship',
        selectedMessaging: {
          message: 'In-store purchase only',
          showLowStock: false,
        },
      };
    }
    return {
      title: 'Ship',
      subText: 'Select product options',
      disabled: false,
      valid: false,
      type: 'ship',
      selectedMessaging: {
        message: 'Select product options for availability.',
        showLowStock: false,
      },
    };
  }

  if (sku.buyable === 0 || sku.shipQty === 0 || sku.shipQty < count) {
    return {
      title: 'Ship',
      subText: 'Not available',
      disabled: true,
      valid: false,
      type: 'ship',
      selectedMessaging: {
        message: 'Not available',
        showLowStock: false,
      },
    };
  }

  return {
    title: 'Ship',
    subText: (
      <>
        {sameDayDeliveryEnabled ? `Delivering to ` : 'Available'}
        {sameDayDeliveryEnabled ? (
          <ActionText
            variant="tertiary"
            size="medium"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.headerFunctions.sameDayDelivery();
            }}>
            {deliveryZip}
          </ActionText>
        ) : null}
      </>
    ),
    disabled: false,
    valid: true,
    type: 'ship',
    selectedMessaging: {
      message: 'Available',
      showLowStock: sku.shipQty !== 0 && sku.shipQty < 10,
      quantity: sku.shipQty,
    },
  };
};

export const getPickupMethodState = (
  storeDetails,
  storeIdentifierName,
  quickviewBoplEnabled,
  quickviewDetails: QuickviewDetails,
  sku: SkuDetails,
  skuAvailability: SkuStoreAvailability[],
  count: number,
  skuList: string[],
): FulfillmentButtonState => {
  // if no buyable skus (in store only)
  if (
    quickviewDetails &&
    quickviewDetails.style &&
    quickviewDetails.style.buyable === 0 &&
    storeDetails
  ) {
    return {
      title: 'Free Store Pickup',
      subText: 'In-store purchase only',
      disabled: true,
      valid: false,
      type: 'bopis',
      selectedMessaging: {
        message: 'In-store purchase only',
        showLowStock: false,
      },
    };
  }

  if (!sku) {
    return {
      title: 'Free Store Pickup',
      subText: 'Select product options',
      disabled: false,
      valid: false,
      type: 'bopis',
      selectedMessaging: {
        message: 'Select product options for availability at',
        showLowStock: false,
      },
    };
  }
  // if no store
  if (!storeDetails) {
    return {
      title: 'Free Store Pickup',
      subText: 'Not available',
      disabled: true,
      valid: false,
      type: 'bopis',
      selectedMessaging: {
        message: 'Please select a store for availability.',
        showLowStock: false,
      },
    };
  }
  // else store
  // current store inventory
  const currentSkuSelectedStoreAvailability =
    sku &&
    sku.partNumber &&
    typeof skuAvailability === 'object' &&
    skuAvailability !== null &&
    sku.partNumber in skuAvailability
      ? skuAvailability[sku.partNumber].find((storeResult) => {
          if (storeDetails.location !== undefined)
            return storeResult.storeRes.location === storeDetails.location;
          return storeResult.storeRes.location === storeDetails.store;
        })
      : null;

  const availableNearby =
    sku &&
    sku.partNumber &&
    typeof skuAvailability === 'object' &&
    skuAvailability !== null &&
    sku.partNumber in skuAvailability &&
    skuAvailability[sku.partNumber].slice(0, 5).length > 0;

  const hasInventory =
    currentSkuSelectedStoreAvailability && currentSkuSelectedStoreAvailability.inventory;

  const hasATS = hasInventory && currentSkuSelectedStoreAvailability.inventory.ats;
  const hasBOPL = hasInventory && currentSkuSelectedStoreAvailability.inventory.bopl;

  // if available BOPIS
  if (hasATS && currentSkuSelectedStoreAvailability.inventory.ats >= 1 && storeDetails) {
    // if qty is in stock
    if (currentSkuSelectedStoreAvailability.inventory.ats >= count) {
      return {
        title: 'Free Store Pickup',
        subText: (
          <>
            In Stock at{' '}
            <span className="hmf-text-transform-capitalize">{storeDetails.name.toLowerCase()}</span>
          </>
        ),
        disabled: false,
        valid: true,
        type: 'bopis',
        selectedMessaging: {
          message: 'Available for pickup at',
          showLowStock:
            currentSkuSelectedStoreAvailability.inventory.ats !== 0 &&
            currentSkuSelectedStoreAvailability.inventory.ats < 10,
          quantity: currentSkuSelectedStoreAvailability.inventory.ats,
          nearbyStores: skuAvailability[sku.partNumber].length,
        },
      };
    }
    // else qty too high
    return {
      title: 'Free Store Pickup',
      subText: 'Not available',
      disabled: true,
      valid: false,
      type: 'bopis',
      selectedMessaging: {
        message: 'Not available',
        showLowStock: false,
      },
    };
  }
  // else if available BOPL && store === DSG
  if (
    quickviewBoplEnabled &&
    hasBOPL &&
    currentSkuSelectedStoreAvailability.inventory.bopl >= 1 &&
    storeDetails
  ) {
    // if qty is in okay
    if (currentSkuSelectedStoreAvailability.inventory.bopl >= count) {
      return {
        title: 'Ship to Store',
        subText: 'Free pickup in 5-10 days',
        disabled: false,
        valid: true,
        type: 'bopl',
        selectedMessaging: {
          message: "We'll Ship Free to",
          showLowStock:
            currentSkuSelectedStoreAvailability.inventory.ats !== 0 &&
            currentSkuSelectedStoreAvailability.inventory.ats < 10,
          quantity: currentSkuSelectedStoreAvailability.inventory.ats,
          nearbyStores: skuAvailability[sku.partNumber].length,
        },
      };
    }
    // else qty too high
    return {
      title: 'Ship to Store',
      subText: 'Not available',
      disabled: true,
      valid: false,
      type: 'bopis',
      selectedMessaging: {
        message: 'Not available',
        showLowStock: false,
      },
    };
  }
  // else not available at store but maybe nearby
  if (availableNearby) {
    return {
      title: 'Free Store Pickup',
      subText: (
        <>
          Not available
          <br />
          <ActionText
            variant="tertiary"
            className="not-available-link"
            size="medium"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.headerFunctions.storeSelection({
                skusArr: skuList,
                selectedSkuVal: sku.partNumber,
              });
            }}>
            See available stores
          </ActionText>
        </>
      ),
      disabled: false,
      valid: false,
      type: 'bopis',
      selectedMessaging: {
        message: 'Not Available for Pickup at',
        showLowStock: false,
        nearbyStores: skuAvailability[sku.partNumber].length,
      },
    };
  }

  return {
    title: 'Free Store Pickup',
    subText: 'Not available',
    disabled: true,
    valid: false,
    type: 'bopis',
    selectedMessaging: {
      message: 'Not Available for Pickup at',
      showLowStock: false,
    },
  };
};

export const getSDDMethodState = (
  sku: SkuDetails,
  skuSameDayAvailability: SameDaySkuAvailabilityDetails[] | null,
  deliveryZip: string,
  count: number,
): FulfillmentButtonState => {
  if (!sku) {
    return {
      title: 'Same Day Delivery',
      subText: 'Select product options',
      disabled: false,
      valid: false,
      type: 'sdd',
      selectedMessaging: {
        message: 'Select product options for availability.',
        showLowStock: false,
      },
    };
  }

  if (skuSameDayAvailability && skuSameDayAvailability.length > 0) {
    // run checks
    const skuSDDData = skuSameDayAvailability.find((sddSku) => {
      return sddSku.sku.toString() === sku.partNumber.toString();
    });

    if (skuSDDData && skuSDDData.sameDayEligible && skuSDDData.sameDayQuantity >= count) {
      return {
        title: 'Same Day Delivery',
        subText: 'Order by 2pm, get it today!',
        disabled: false,
        valid: true,
        type: 'sdd',
        selectedMessaging: {
          message: (
            <>
              Same Day Delivery to{' '}
              <ActionText
                variant="primary"
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.headerFunctions.sameDayDelivery();
                }}>
                {deliveryZip}
              </ActionText>
            </>
          ),
          showLowStock: skuSDDData.sameDayQuantity !== 0 && skuSDDData.sameDayQuantity < 10,
          quantity: skuSDDData.sameDayQuantity,
        },
      };
    }
  }

  return {
    title: 'Same Day Delivery',
    subText: (
      <>
        Not available to{' '}
        <ActionText
          variant="tertiary"
          className="label-disabled"
          size="medium"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.headerFunctions.sameDayDelivery();
          }}>
          {deliveryZip}
        </ActionText>
      </>
    ),
    disabled: true,
    valid: false,
    type: 'sdd',
    selectedMessaging: {
      message: (
        <>
          Not Available for Same Day Delivery to{' '}
          <ActionText
            variant="primary"
            size="medium"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.headerFunctions.sameDayDelivery();
            }}>
            {deliveryZip}
          </ActionText>
        </>
      ),
      showLowStock: false,
    },
  };
};
